import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { brand } from "../lib/brand";
import { CATEGORIES, sortFeed, timeAgo } from "../lib/community";
import { questionAuthorLabel } from "../lib/qa";
import {
  createCommunityPost,
  getRecentLessonQuestions,
  setPostLiked,
  watchCommunityPosts,
  watchMembers,
  watchMyLikes,
  type CommunityMember,
  type CommunityPost,
  type FeedQuestion,
} from "../lib/firebase";
import { MemberAvatar } from "../components/CommunityGate";

export function CommunityPage() {
  const { user, isAdmin, displayName } = useAuth();
  const [posts, setPosts] = useState<CommunityPost[] | null>(null);
  const [members, setMembers] = useState<Map<string, CommunityMember>>(new Map());
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<string | null>(null);
  const [questions, setQuestions] = useState<FeedQuestion[]>([]);

  useEffect(() => watchCommunityPosts(brand.id, setPosts), []);
  useEffect(() => watchMembers((ms) => setMembers(new Map(ms.map((m) => [m.uid, m])))), []);
  useEffect(() => (user ? watchMyLikes(user.uid, setLiked) : undefined), [user]);
  // Questions asked inside lessons show up here too; they live under the ถาม-ตอบ filter.
  useEffect(() => {
    if (user) getRecentLessonQuestions(user.uid, isAdmin).then(setQuestions).catch(() => setQuestions([]));
  }, [user, isAdmin]);

  const visible = useMemo(
    () =>
      sortFeed([
        ...(posts ?? []).filter((p) => !filter || p.category === filter).map((p) => ({ ...p, kind: "post" as const })),
        ...(!filter || filter === "ถาม-ตอบ" ? questions : []).map((q) => ({ ...q, kind: "question" as const, pinned: false })),
      ]),
    [posts, questions, filter]
  );

  if (!user) return null;

  return (
    <main className="container-tight">
      <Composer
        // Only the instructor posts announcements.
        categories={CATEGORIES.filter((c) => isAdmin || c !== "ประกาศ")}
        onPost={(data) => createCommunityPost({ uid: user.uid, displayName }, { ...data, brand: brand.id })}
        authorName={displayName}
        authorAvatar={members.get(user.uid)?.avatar}
      />

      <div className="cm-chips" role="group" aria-label="กรองตามหมวด">
        <button className={`cm-chip ${filter === null ? "cm-chip--on" : ""}`} onClick={() => setFilter(null)}>
          ทั้งหมด
        </button>
        {CATEGORIES.map((c) => (
          <button key={c} className={`cm-chip ${filter === c ? "cm-chip--on" : ""}`} onClick={() => setFilter(c)}>
            {c}
          </button>
        ))}
      </div>

      {posts === null && <p style={{ color: "var(--c-text-muted)" }}>กำลังโหลด…</p>}
      {posts !== null && visible.length === 0 && (
        <div className="empty-state"><p>ยังไม่มีโพสต์ในหมวดนี้ เริ่มโพสต์แรกเลย</p></div>
      )}

      <ul className="cm-feed">
        {visible.map((p) =>
          p.kind === "question" ? (
            <li key={`q-${p.id}`}>
              <QuestionCard q={p} />
            </li>
          ) : (
            <li key={p.id}>
              <PostCard
                post={p}
                author={members.get(p.authorUid)}
                liked={liked.has(p.id)}
                onLike={(on) => setPostLiked(p.id, user.uid, on)}
              />
            </li>
          )
        )}
      </ul>
    </main>
  );
}

/** A lesson question in the feed. Anonymous askers stay anonymous (questionAuthorLabel). */
function QuestionCard({ q }: { q: FeedQuestion }) {
  const link = `/courses/${q.courseId}/lessons/${q.lessonId}#q-${q.id}`;
  return (
    <article className="cm-post">
      <header className="cm-post__head">
        <MemberAvatar name={q.isAnonymous ? "?" : questionAuthorLabel(q)} />
        <div>
          <strong>{questionAuthorLabel(q)}</strong>
          <div className="cm-post__meta">
            {timeAgo(q.createdAt)} · ❓ ถามในบทเรียน: {q.lessonTitle || "บทเรียน"}
            {!q.answerText && <span className="cm-post__pin cm-post__pin--wait">รอตอบ</span>}
          </div>
        </div>
      </header>
      <Link to={link} className="cm-post__title">{q.text.length > 160 ? q.text.slice(0, 160).trimEnd() + "…" : q.text}</Link>
      {q.answerText && (
        <p className="cm-post__answer">
          <strong>Dewviko AI (AI Coach):</strong> {q.answerText.length > 200 ? q.answerText.slice(0, 200).trimEnd() + "…" : q.answerText}
        </p>
      )}
      <footer className="cm-post__actions">
        <Link to={link}>ไปดูในบทเรียน →</Link>
      </footer>
    </article>
  );
}

function Composer({
  categories,
  onPost,
  authorName,
  authorAvatar,
}: {
  categories: string[];
  onPost: (d: { category: string; title: string; body: string }) => Promise<unknown>;
  authorName: string;
  authorAvatar?: CommunityMember["avatar"];
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState(categories[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return setError("ใส่หัวข้อก่อนนะ");
    setBusy(true);
    setError(null);
    try {
      await onPost({ category, title: title.trim(), body: body.trim() });
      setTitle("");
      setBody("");
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="cm-composer cm-composer--closed" onClick={() => setOpen(true)}>
        <MemberAvatar name={authorName} avatar={authorAvatar} />
        <span>เขียนอะไรสักอย่าง…</span>
      </button>
    );
  }
  return (
    <form className="cm-composer" onSubmit={submit}>
      <input
        autoFocus
        placeholder="หัวข้อ"
        value={title}
        maxLength={200}
        onChange={(e) => { setTitle(e.target.value); setError(null); }}
      />
      <textarea placeholder="เล่าให้เพื่อนๆ ฟัง…" value={body} maxLength={10000} onChange={(e) => setBody(e.target.value)} />
      {error && <p className="error-inline">{error}</p>}
      <div className="cm-composer__actions">
        <select value={category} onChange={(e) => setCategory(e.target.value)} aria-label="หมวด">
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>ยกเลิก</button>
        <button type="submit" className="btn-primary" disabled={busy}>{busy ? "กำลังโพสต์…" : "โพสต์"}</button>
      </div>
    </form>
  );
}

export function PostCard({
  post,
  author,
  liked,
  onLike,
  full = false,
}: {
  post: CommunityPost;
  /** The author's member doc (level + avatar); undefined if they left the community. */
  author?: CommunityMember;
  liked: boolean;
  onLike: (on: boolean) => void;
  full?: boolean;
}) {
  const body = full || post.body.length <= 280 ? post.body : post.body.slice(0, 280).trimEnd() + "…";
  return (
    <article className={`cm-post ${post.pinned ? "cm-post--pinned" : ""}`}>
      <header className="cm-post__head">
        <MemberAvatar name={post.authorName} points={author?.points} avatar={author?.avatar} />
        <div>
          <strong>{post.authorName}</strong>
          <div className="cm-post__meta">
            {timeAgo(post.createdAt)} · {post.category}
            {post.pinned && <span className="cm-post__pin">ปักหมุด</span>}
          </div>
        </div>
      </header>
      {full ? <h1 className="cm-post__title">{post.title}</h1> : (
        <Link to={`/chat/${post.id}`} className="cm-post__title">{post.title}</Link>
      )}
      {body && <p className="cm-post__body">{body}</p>}
      <footer className="cm-post__actions">
        <button
          className={`cm-like ${liked ? "cm-like--on" : ""}`}
          aria-pressed={liked}
          onClick={() => onLike(!liked)}
        >
          👍 {post.likeCount}
        </button>
        {full ? <span>💬 {post.commentCount}</span> : <Link to={`/chat/${post.id}`}>💬 {post.commentCount} ความเห็น</Link>}
      </footer>
    </article>
  );
}
